import { Link, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ReadingPlan } from '../../shared/api/types';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { MyReadingGroups } from './ReadTogether';

const STATUS: Record<ReadingPlan['status'], string> = { draft: 'Borrador', published: 'Publicado', archived: 'Archivado' };

export function progressLabel(p: ReadingPlan): string | null {
  if (!p.mine) return null;
  if (p.mine.status === 'finished') return 'Terminado';
  if (p.mine.status !== 'active') return null;
  return `Día ${p.mine.today} de ${p.totalDays} · ${p.mine.completed.length} ${p.mine.completed.length === 1 ? 'leído' : 'leídos'}`;
}

/** Devocional (Motor 14): planes de lectura de la iglesia. Tu avance solo lo ves tú. */
export function DevotionalPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('devotional.manage');
  const [params, setParams] = useSearchParams();
  const manage = canManage && params.get('vista') === 'administrar';
  const plans = useAsync(
    (signal) => (churchId ? api.get<{ items: ReadingPlan[] }>(`/devotional/churches/${churchId}/plans?manage=${manage}`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, manage],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const mineFirst = plans.status === 'ready' ? [...plans.data].sort((a, b) => Number(b.mine?.status === 'active') - Number(a.mine?.status === 'active')) : [];

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Planes de lectura</h1>
        <p className="muted">Lee la Biblia con tu iglesia, un día a la vez. Tu avance solo lo ves tú.</p>
      </header>
      {!manage ? <MyReadingGroups /> : null}
      {canManage ? (
        <div className="tabs" role="tablist" aria-label="Planes">
          <button type="button" role="tab" aria-selected={!manage} className={`tabs__tab${!manage ? ' tabs__tab--active' : ''}`} onClick={() => setParams({})}>
            Para leer
          </button>
          <button type="button" role="tab" aria-selected={manage} className={`tabs__tab${manage ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ vista: 'administrar' })}>
            Administrar
          </button>
        </div>
      ) : null}
      {manage ? (
        <div className="actions">
          <Link className="btn btn--primary" to="/app/devocional/nuevo">
            <span>Nuevo plan</span>
          </Link>
          <Link className="btn btn--secondary" to="/app/devocional/hoy">
            <span>Preparar el Hoy de la iglesia</span>
          </Link>
        </div>
      ) : null}
      {plans.status === 'loading' ? <Loading /> : null}
      {plans.status === 'error' ? <ErrorState error={plans.error} onRetry={plans.reload} /> : null}
      {plans.status === 'ready' && plans.data.length === 0 ? <EmptyState title={manage ? 'Aún no hay planes.' : 'Tu iglesia aún no publica planes de lectura.'} /> : null}
      {mineFirst.length > 0 ? (
        <ul className="list card">
          {mineFirst.map((p) => (
            <li key={p.id} className="list__item">
              <Link className="list__link" to={`/app/devocional/${p.id}`}>
                <strong>{p.title}</strong>
                <span className="muted small">
                  {p.totalDays} {p.totalDays === 1 ? 'día' : 'días'}
                  {manage ? ` · ${STATUS[p.status]} · ${p.enrolled ?? 0} lo siguen` : ''}
                  {progressLabel(p) ? ` · ${progressLabel(p)}` : ''}
                </span>
                {p.description ? <span className="small">{p.description}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
